import type { User } from 'oidc-client-ts';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';

import { stashPostSignInPath } from './postSignInPath';
import type { Session } from './session';
import { SessionContext } from './session';
import { tokenRefresh, userManager } from './userManager';

/** Adapts react-oidc-context onto the app's Session interface. */
const OidcSessionBridge = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();

  // Expired-on-load recovery: react-oidc-context reports a stored-but-expired
  // user as unauthenticated, and the library never schedules renewal for an
  // already-expired token — yet the refresh token in the same stored user is
  // typically still valid (30-day absolute lifetime). One guarded silent
  // renewal turns "sign in again after an hour away" into a silent recovery.
  // The per-token ref makes it one attempt per stored token, never a loop:
  // success replaces the token; fatal failure removes the user entirely.
  const recoveryAttemptedRef = useRef<string | null>(null);
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    const token = auth.user?.access_token;

    if (
      auth.user?.expired &&
      token &&
      !auth.isLoading &&
      !auth.activeNavigator &&
      recoveryAttemptedRef.current !== token
    ) {
      recoveryAttemptedRef.current = token;
      setRecovering(true);
      void tokenRefresh.getFreshAccessToken({ forceRenew: true }).finally(() => {
        setRecovering(false);
      });
    }
  }, [auth.user, auth.isLoading, auth.activeNavigator]);

  const session = useMemo<Session>(
    () => ({
      isLoading: auth.isLoading || recovering,
      isAuthenticated: auth.isAuthenticated,
      mode: 'oidc',
      ...(auth.user
        ? {
            user: {
              displayName: auth.user.profile.name ?? auth.user.profile.sub,
              subject: auth.user.profile.sub,
            },
          }
        : {}),
      ...(auth.error ? { error: auth.error.message } : {}),
      signIn: () => {
        void auth.signinRedirect({ url_state: window.location.pathname });
      },
      signOut: () => {
        void auth.signoutRedirect();
      },
      clearSession: () => {
        void userManager.removeUser();
      },
      getAccessToken: (opts) => tokenRefresh.getFreshAccessToken(opts),
    }),
    [auth, recovering],
  );

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
};

const onSigninCallback = (user: User | undefined): void => {
  // Remember where the user was before the redirect (sent as url_state), then
  // strip the auth payload out of the address bar once the code has been
  // exchanged. CallbackRoute consumes the stash.
  stashPostSignInPath(user?.url_state, window.sessionStorage);
  window.history.replaceState({}, '', window.location.pathname);
};

export const OidcSessionProvider = ({ children }: { children: ReactNode }) => (
  <AuthProvider userManager={userManager} onSigninCallback={onSigninCallback}>
    <OidcSessionBridge>{children}</OidcSessionBridge>
  </AuthProvider>
);
